// ADR 5.9.26p §2/§5 — the auth endpoints, one function each.
//
// Every request body here is the phones' body byte for byte
// (`rust-core/src/client/client_auth.rs`) with one deliberate subtraction:
// `device_id`. Neither `LoginRequest` nor `RegisterRequest` declares it
// (`HSKVocabBackend/crates/core/src/models/user.rs:22-40`), so the value the
// phones send is dropped by serde on arrival. Sending a field the server
// discards would only invite a reader to believe it means something (AC8).

import { authed, authedJson, requestJson } from './http'
import { readRefreshToken } from '../auth/tokenStore'

/** The envelope login, register, social **and** refresh all return. */
export interface AuthResponse {
  token: string
  refresh_token: string
  user_id: string
  email_verified: boolean
}

/** `GET /auth/challenge` — `services/pow.rs:56`. */
export interface PowChallenge {
  nonce: string
  difficulty: number
  ttl_seconds: number
}

/** The `POST /auth/login` body. `auth_version` is 2 — the prehash generation. */
export interface LoginRequest {
  email: string
  prehash: string
  auth_version: number
  pow_nonce?: string
  pow_solution?: string
}

/**
 * The `POST /auth/register` body: the login body plus the terms box.
 *
 * `accepted_terms` is `#[serde(default)]` on the server and rejected when
 * `false`, so it is not an optional field with a friendly default — omitting it
 * is a 400 (AC8).
 */
export interface RegisterRequest extends LoginRequest {
  accepted_terms: true
}

/**
 * The `/features` fields this client reads.
 *
 * The endpoint returns some seventy evaluated flags; naming only what is used
 * keeps a reader from assuming the web consumes the rest. Tier and trial state
 * live *inside* these flags — `/features` carries no `email_verified`, which is
 * why it is never the source of that bit (§5).
 */
export interface FeatureFlags {
  has_paid_access: boolean
  trial_active: boolean
  bypass_subscription: boolean
  trial_days_remaining?: number | null
  /**
   * ADR 5.9.26r §4 — the deck's gate, read here rather than discovered as a
   * 403. The server gates only `create`, `list` and `due` on it
   * (`routes/flashcards/mod.rs:29,82,174`); the web gates the whole surface,
   * which is why it has to be a flag the page can read before it asks.
   */
  enable_spaced_rep: boolean
  /**
   * ADR 5.9.26s §1 — the tutor's master flag, one of the two inputs
   * `tutor_fab_state` ranks. Read here for the same reason as the deck's flag:
   * a dark tutor is a route that redirects, not a 404 the panel discovers.
   */
  enable_ai_tutor: boolean
  /** Tutor interaction capture is dark by default and evaluated per account. */
  enable_tutor_event_capture?: boolean
  /**
   * ADR 7.9.26k §3 — the share fold's gate, read here rather than discovered
   * as a 403.
   *
   * Optional, unlike the two above, and deliberately: the fold's cheapest case
   * is the key being *absent*, which folds to `hidden` with no request at all
   * — so a suite that stubs `/features` without it (every existing plan-detail
   * suite) keeps passing and never fires a share request nobody expected.
   */
  enable_shared_plans?: boolean
  /**
   * ADR 10.9.26f §5 — checkout's kill switch, read here rather than discovered
   * as a 403.
   *
   * Already on the wire (`EvaluatedFlags` carries `pub enable_stripe: bool`,
   * `feature_flags_model.rs:77`); only this interface omitted it. Optional in
   * `enable_shared_plans`'s shape and for its reason: an absent key folds to
   * "no upgrade route" with no `/subscriptions/plans` request at all, so every
   * suite that stubs `/features` without it keeps passing. It gates the
   * *entrance* only — `create` and `change-plan` bill; `portal`, `cancel` and
   * `reactivate` never read it server-side and are never withheld here.
   */
  enable_stripe?: boolean
  /**
   * ADR 8.9.26h — one boolean per visual space, read rather than discovered as
   * an empty render.
   *
   * They arrive **already composed**: the server sends `master ||
   * enable_insitu_visuals` per space (`feature_flags_eval.rs:83-85`), so the
   * web composes nothing for the *base* slots.
   *
   * Optional for the same reason `enable_shared_plans` is: an absent key folds
   * to `hidden` with no image request at all, so every suite that stubs
   * `/features` without them keeps rendering exactly today's page.
   */
  enable_lesson_visuals?: boolean
  /** ADR 8.9.26h — the reference-passage space's composed lever. */
  enable_narrative_visuals?: boolean
  /** ADR 8.9.26h — the exercise-set-theme space's composed lever. */
  enable_set_theme_visuals?: boolean
  /**
   * ADR 15.9.26g §2 — the progressive layer's lever, which 8.9.26h declined
   * "until the layer arrives": the poll window opens on `enable_insitu_visuals
   * || ondemandArmed` (24.7.26e D3a), read by `useVisualsPoll`. Already on the
   * wire (`feature_flags_model.rs:130`). Optional in `enable_stripe`'s shape:
   * an absent key folds to `feature_off` and no poll request at all.
   */
  enable_insitu_visuals?: boolean
  /**
   * ADR 15.9.26g §2 — the on-demand trigger's operational flag (24.7.26e D3d):
   * with it on, a paid user's imageless plan POSTs `/lesson-plans/{id}/visuals`
   * once per mount. Absent folds to "never fire" — no request.
   */
  enable_ondemand_visuals?: boolean
  /**
   * ADR 10.9.26h §1 — the thumbs' flag, half of `vocab_feedback_affordance_visible`'s
   * `seam_wired` (flag on **and** authenticated; `VocabFeedbackSection.kt:57`).
   *
   * Already on the wire (`feature_flags_model.rs:103`); only this interface
   * omitted it. Optional in `enable_stripe`'s shape and for its reason: an
   * absent key folds to "no thumbs" with no feedback request at all, so every
   * suite that stubs `/features` without it keeps rendering today's page.
   */
  enable_vocab_feedback?: boolean
  /** ADR 16.9.26p §2 — Play mode's gate (8.7.26g §3); absent folds to "no Play control", no request. */
  enable_play_mode?: boolean
  /** ADR 16.9.26t §3 — the flashcards coach card (15.8.26g D5); absent folds to hidden, no request. */
  enable_flashcards_coach?: boolean
  /** ADR 16.9.26p §3 — the walkthrough's "Create follow-up" CTA (8.7.26g §7); absent folds to hidden. */
  enable_remix?: boolean
  /**
   * ADR 17.9.26j D2 — may this client render an ad slot? The server's derived
   * word (Backend 16.9.26d: `enable_ads`, forced `false` for every paid
   * status; on the anonymous envelope too since 17.9.26j D9). Optional in
   * `enable_remix`'s shape: an absent key is `Hidden`, so no suite that stubs
   * `/features` without it ever contacts Google.
   */
  show_ads?: boolean
  /** ADR 25.9.26k §1 — the `/admin` console's gate (O1, per user); absent folds to hidden, no request. */
  enable_oauth_clients?: boolean
  /** ADR 30.9.26y §1 — `/admin/webhooks`' gate (E1, per user); absent folds to "Soon" and a not-found page, no request. */
  enable_webhooks?: boolean
  /** ADR 30.9.26ai §1 — `/admin/apps` and both slots (A1, per user); absent folds to "Soon", no slot, no request. */
  enable_apps?: boolean
  /** ADR 30.9.26ap §1 — the `/apps` shelf and the `listed` box (A1, per user); absent folds to not found, no request. */
  enable_app_marketplace?: boolean
  /** ADR 1.10.26v §12 — the embed scopes and the embed-origins field (B1's `enable_embed`); absent folds to off. */
  enable_embed?: boolean
  /** ADR 2.10.26d §8 — the connections builder (Backend 2.10.26c §5, per user); declared here, read by C5. */
  enable_connections_builder?: boolean
  /** Backend 25.9.26n §11 — redirect URIs and public clients on the console's forms; absent folds to hidden. */
  enable_oauth_authorization_code?: boolean
  /** ADR 23.9.26y §3 — the web reminder row's gate (Backend 23.9.26x §8); absent folds to hidden, no request. */
  web_push_reminders_enabled?: boolean
  /** ADR 28.9.26e §5 — Home's teaching style (`PersonalityFlags`); absent folds to `hidden_flag_off`, no request. */
  enable_personalities?: boolean
  /** ADR 28.9.26e §5 — the testing override that unlocks every style for a free account. */
  free_personality_access?: boolean
  /** Server-evaluated languages whose cloud TTS provider is live; empty today. */
  cloud_tts_languages?: string[]
  /**
   * ADR 15.9.26o §4 — the mode the box is in (Backend 15.9.26j, on both
   * `/features` envelopes). Read by `features/serviceMode/` for the banner
   * on the two surfaces whose button 15.9.26j's table refuses by class.
   * Optional: an older backend omits it, and nothing renders.
   */
  service_mode?: ServiceMode
  /** ADR 3.10.26a §2 — the level caps `hskLevelCeiling` folds; absent falls to rust-core's defaults. */
  free_max_hsk?: number
  auth_max_hsk?: number
}

/** 15.9.26j's four labels, as `ServiceMode::as_label()` spells them. */
export type ServiceMode = 'normal' | 'constrained' | 'severe' | 'emergency'

/**
 * The envelope `GET /features` actually returns (ADR 8.9.26e §1).
 *
 * `rust-core` decodes the same wrapper (`api_models.rs:199-204`) and
 * `client_repo.rs:139-142` explains why: the inner object alone fails on every
 * request. The web read the inner type off the outer body for four releases,
 * so every flag came back `undefined` and every gate coalesced to its
 * free-tier arm.
 *
 * `evaluated_at` is declared and unread on purpose: naming it is what makes
 * the next reader see the shape.
 */
export interface FlagsEnvelope {
  flags: FeatureFlags
  evaluated_at: string
}

/**
 * The whole of the fix, extracted so the golden replay can call it without a
 * network.
 *
 * It looks like ceremony for a property access, and the extraction is the
 * point: every other web-side golden replay drives a pure function. Left
 * inside `features()`, the subject would sit behind `authed()` and the test
 * would need msw, a `/auth/refresh` stub and a seeded refresh token to
 * exercise one line.
 */
export function flagsFromEnvelope(envelope: FlagsEnvelope): FeatureFlags {
  return envelope.flags
}

export function challenge(): Promise<PowChallenge> {
  return requestJson<PowChallenge>('/auth/challenge')
}

export function login(body: LoginRequest): Promise<AuthResponse> {
  return requestJson<AuthResponse>('/auth/login', { method: 'POST', body })
}

export function register(body: RegisterRequest): Promise<AuthResponse> {
  return requestJson<AuthResponse>('/auth/register', { method: 'POST', body })
}

/** A server-minted social sign-in nonce (ADR 24.9.26v §1). */
export interface SocialNonce {
  nonce: string
  ttl_seconds: number
}

/** `POST /auth/social/nonce`, or `null` on any failure: the server then decides (24.9.26v §4). */
export function fetchSocialNonce(): Promise<SocialNonce | null> {
  return requestJson<SocialNonce>('/auth/social/nonce', { method: 'POST', body: {} }).catch(() => null)
}

/** `POST /auth/social` — the GIS `credential` as `id_token` (AC2), plus the round's nonce if any. */
export function social(idToken: string, nonce: string | null): Promise<AuthResponse> {
  return requestJson<AuthResponse>('/auth/social', {
    method: 'POST',
    body: { provider: 'google', id_token: idToken, ...(nonce === null ? {} : { nonce }) },
  })
}

/**
 * `POST /auth/logout` — revokes the whole token family.
 *
 * The route needs a valid bearer **and** the refresh token in the body
 * (`refresh_tokens.rs:200-215`), so it must be sent before either is cleared;
 * `signOut` in `auth/session.tsx` is what guarantees that order. Answers 204,
 * so there is no body to decode.
 */
export async function logout(): Promise<void> {
  const refreshToken = readRefreshToken()
  if (refreshToken === null) return
  await authed('/auth/logout', { method: 'POST', body: { refresh_token: refreshToken } })
}

/**
 * `GET /features` — the envelope, unwrapped (ADR 8.9.26e §1).
 *
 * The unwrap lands here rather than at either storage site because
 * `refreshFlags` (`auth/session.tsx:166-176`) re-reads through this same
 * function: fixing one storage site would leave the mid-session re-read still
 * writing `undefined`s.
 */
export async function features(): Promise<FeatureFlags> {
  return flagsFromEnvelope(await authedJson<FlagsEnvelope>('/features'))
}

/**
 * `GET /features`, the anonymous arm (ADR 17.9.26j D2) — no bearer and no device token, so a stranger's
 * `show_ads` and level caps cost no correlation. Called from `features/ads/useAdSlot` and (ADR 3.10.26a §2)
 * `features/settings/useLevelCeiling`, under one query key, once per document; never from `auth/session.tsx`. */
export async function anonymousFeatures(): Promise<FeatureFlags> {
  return flagsFromEnvelope(await requestJson<FlagsEnvelope>('/features'))
}

/**
 * `POST /auth/refresh`.
 *
 * The request itself lives in `http.ts`, which is what keeps `tokenStore.ts`
 * below it in the import graph (see that module's header). It is re-exported
 * here so this file is the whole endpoint catalogue rather than six-sevenths
 * of one.
 */
export { refreshSession as refresh } from './http'
